import {
  Avatar,
  Box,
  Button,
  FormControl,
  Grid2,
  MenuItem,
  Modal,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  CaretLeft,
  CaretRight,
  CheckSquareOffset,
  Eye,
  EyeClosed,
  EyeSlash,
  Gift,
  Info,
  Medal,
  Notebook,
  Trash,
  X,
} from "phosphor-react";
import React, { useEffect, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { fdelete, fget } from "../../../../API/callsAPI";
import commonStyles from "../../../../Utils/commonStyles";
import DateAndTimeViewer from "../../../Utilities/DateAndTimeViewer/DateAndTimeViewer.component";
import DeleteModal from "../../../Utilities/DeleteModal/DeleteModal.component";
import { getNumberWithSuffix } from "../../../utils";
import UserScoreRankingTable from "./UserScoreRankingTable";
import userScoreStyles from "./UserScoreStyles";
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from "@material-ui/pickers";
import moment from "moment";
import MomentUtils from "@date-io/moment";
import { generateDateFormat } from "../../../../Utils/generateDateFormat";

const columns = [
  { id: "name", label: "Points", minWidth: 170 },
  { id: "code", label: "Received On", minWidth: 100 },
  {
    id: "population",
    label: "Received From",
    minWidth: 170,
    align: "center",
    format: (value) => value.toLocaleString("en-US"),
  },
  {
    id: "size",
    label: "Trackable",
    minWidth: 170,
    align: "right",
    format: (value) => value.toLocaleString("en-US"),
  },
  {
    id: "size",
    label: "",
    minWidth: 170,
    align: "right",
    format: (value) => value.toLocaleString("en-US"),
  },
];

const UserScoreExpandedView = ({
  handleClose,
  projectId,
  userRoleForProject,
  loggedInUser,
}) => {
  const commonClasses = commonStyles();
  const classes = userScoreStyles();
  const [showChart, setShowChart] = useState(true);
  const [selectedTab, setSelectedTab] = useState("score");
  const [userScoreDetails, setUserScoreDetails] = useState({});
  const [monthlyScores, setMonthlyScores] = useState([]);
  const [dates, setDates] = useState([]);
  const [deleteScoreData, setDeleteScoreData] = useState({
    isModalOpen: false,
    selectedScore: null,
  });
  const [selectedStartDate, setSelectedStartDate] = useState(() => {
    const today = new Date();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(today.getDate() - 7); // Subtract 7 days from today
    return sevenDaysAgo;
  });
  const [selectedEndDate, setSelectedEndDate] = useState(new Date());
  const getUserScore = async () => {
    try {
      const res = await fget({
        url: `user-score/?project=${projectId}`,
      });
      setUserScoreDetails(res?.data?.results[0]);
    } catch (error) {
      console.log(error);
    }
  };

  const getUserScoreTimeline = async () => {
    try {
      const res = await fget({
        url: `score-timeline/?project=${projectId}&start=${generateDateFormat(
          selectedStartDate
        )}&end=${generateDateFormat(selectedEndDate)}`,
      });
      setMonthlyScores(res?.data?.map((sin) => sin.score));
      setDates(res?.data?.map((sin) => sin.date));
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getUserScore();
  }, []);
  useEffect(() => {
    getUserScoreTimeline();
  }, [selectedStartDate, selectedEndDate]);
  const options = {
    chart: {
      type: "bar",
      toolbar: { show: false },
      background: "#252A38",
      borderRadius: 2,
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "60%",
        borderRadius: 0,
      },
    },
    dataLabels: {
      enabled: false,
    },
    xaxis: {
      categories: dates,
      labels: {
        style: {
          colors: "#FFFFFF",
        },
      },
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
    },
    yaxis: {
      labels: {
        style: {
          colors: "#FFFFFF",
        },
      },
    },
    grid: {
      show: false,
      borderColor: "#444",
    },
    tooltip: {
      theme: "dark", // Dark tooltip theme
    },
    colors: ["#8B5CF6"], // Purple bar color
  };

  const series = [
    {
      name: "Data",
      data: monthlyScores,
    },
  ];

  const handleDeleteScore = async () => {
    try {
      await fdelete({
        url: `score-record/${deleteScoreData?.selectedScore?.id}/`,
      });
      setDeleteScoreData({
        isModalOpen: false,
        selectedScore: null,
      });
      getUserScore();
    } catch (error) {
      console.log(error);
    }
  };

  const commonTableCell = {
    color: "white",
    borderBottom: "1px solid gray",
    fontSize: {
      sm: "10px",
      md: "10px",
      lg: "12px",
      xl: "14px",
    },
  };

  return (
    <Grid2 container spacing={2} width="100%">
      {/* left side bar start  */}
      <Grid2 size={3}>
        <Box
          display="flex"
          flexDirection="column"
          gap={1}
          borderRight="1px solid #606479"
          pr={1}
          height="100%"
        >
          <Box className={classes.avatarContainer}>
            <Avatar className={classes.avatar}>
              {loggedInUser?.first_name?.split(" ")[0][0]
                ? loggedInUser?.first_name?.split(" ")[0][0]?.toUpperCase()
                : ""}
              {loggedInUser?.last_name?.split(" ")[0][0]
                ? loggedInUser?.last_name?.split(" ")[0][0]?.toUpperCase()
                : ""}
            </Avatar>
            <Typography>
              {loggedInUser?.first_name} {loggedInUser?.last_name}
            </Typography>
          </Box>

          <Box className={commonClasses.flexCenter}>
            <UserSvg />
          </Box>
          <Box
            display="flex"
            alignItems="center"
            gap={2}
            bgcolor="#252A38"
            p={1}
            borderRadius={1}
          >
            <Typography
              px={1.5}
              py={1}
              bgcolor="#6C65D9"
              borderRadius={1}
              sx={{
                fontSize: "22px !important",
                minWidth: "40px",
              }}
            >
              {userScoreDetails?.score ?? 0}
            </Typography>
            <Typography
              sx={{
                fontSize: { md: "10px", lg: "12px ", xl: "14px" },
              }}
            >{`User's project score is excellent!`}</Typography>
          </Box>
          <Box
            display="flex"
            alignItems="center"
            gap={2}
            bgcolor="#252A38"
            p={1}
            borderRadius={1}
          >
            <Box
              bgcolor="#1B1D28"
              p={1}
              borderRadius={1}
              className={commonClasses.flexCenter}
            >
              <Medal size={22} color="#6C65D9" />
            </Box>
            <Typography
              sx={{ fontSize: { lg: "12px", xl: "14px" } }}
            >{`You’re ranked ${getNumberWithSuffix(
              userScoreDetails?.project_ranking
            )} among other users in your dept.`}</Typography>
          </Box>
          <Box
            className={commonClasses.flexBetween}
            bgcolor="#252A38"
            p={1}
            borderRadius={1}
          >
            <Box display="flex" alignItems="center" gap={2}>
              <Box
                bgcolor="#1B1D28"
                p={1}
                borderRadius={1}
                className={commonClasses.flexCenter}
              >
                <CheckSquareOffset size={22} color="#6C65D9" />
              </Box>
              <Typography sx={{ fontSize: { lg: "12px", xl: "14px" } }}>
                Task Completed
              </Typography>
            </Box>
            <Typography sx={{ fontSize: { lg: "14px", xl: "16px" } }} pr={1}>
              {userScoreDetails?.tasks_score ?? 0}
            </Typography>
          </Box>
          {/* <Typography
            variant="h5"
            sx={{
              fontSize: { lg: "18px", xl: "20px" },
            }}
          >
            Score Details
          </Typography>
          <Box
            className={commonClasses.flexBetween}
            bgcolor="#252A38"
            p={1}
            borderRadius={1}
          >
            <Box display="flex" alignItems="center" gap={2}>
              <CheckSquareOffset size={18} />
              <Typography
                sx={{
                  fontSize: { lg: "12px", xl: "14px" },
                }}
              >
                From Tasks
              </Typography>
            </Box>
            <Typography
              sx={{
                fontSize: { lg: "12px", xl: "14px" },
              }}
            >
              {userScoreDetails?.tasks_score ?? 0}
            </Typography>
          </Box>
          <Box
            className={commonClasses.flexBetween}
            bgcolor="#252A38"
            p={1}
            borderRadius={1}
          >
            <Box display="flex" alignItems="center" gap={2}>
              <Gift size={18} />
              <Typography
                sx={{
                  fontSize: { lg: "12px", xl: "14px" },
                }}
              >
                From Gift
              </Typography>
            </Box>
            <Typography
              sx={{
                fontSize: { lg: "12px", xl: "14px" },
              }}
            >
              {userScoreDetails?.gifts_score ?? 0}
            </Typography>
          </Box> */}
        </Box>
      </Grid2>
      {/* left side bar end  */}
      {/* right side component start  */}
      <Grid2 size={9}>
        <Box display="flex" flexDirection="column" gap={2}>
          {/* swap buttons  */}
          <Box className={commonClasses.flexBetween}>
            <Box className={commonClasses.flexAndGap8Px}>
              <Button
                startIcon={<Notebook />}
                variant="contained"
                color="#F9F9F9"
                sx={
                  selectedTab === "score"
                    ? { bgcolor: "#252A38" }
                    : { bgcolor: "transparent", color: "#606479 !important" }
                }
                onClick={() => setSelectedTab("score")}
              >
                Score Logs
              </Button>
              <Button
                variant="contained"
                sx={
                  selectedTab === "ranking"
                    ? { bgcolor: "#252A38" }
                    : { bgcolor: "transparent", color: "#606479 !important" }
                }
                startIcon={<Medal />}
                onClick={() => setSelectedTab("ranking")}
              >
                Rankings
              </Button>
            </Box>
            <Button
              onClick={handleClose}
              sx={{ color: "white !important", minWidth: 0, padding: 0 }}
            >
              <X size={22} />
            </Button>
          </Box>
          {/* middle part  */}
          <Box className={commonClasses.flexBetween}>
            <Box className={commonClasses.flexAndGap8Px}>
              {/* <Typography style={{ minWidth: "100px" }}>Scores Per</Typography> */}
              {/*  <FormControl fullWidth sx={{ minWidth: "120px" }}>
                <Select
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  value="weekly"
                  // onChange={handleChange}
                  sx={{
                    backgroundColor: "#252A38",
                    color: "#fff",
                    borderRadius: "8px",
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      border: "1px solid gray",
                    },
                  }}
                >
                  {["weekly", "monthly"].map((sin) => (
                    <MenuItem
                      value={sin}
                      sx={{
                        backgroundColor: "#1B1D28",
                        color: "#fff",
                        padding: "4px 8px",
                        "&:hover": {
                          backgroundColor: "gray",
                        },
                      }}
                    >
                      {sin}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl> */}
              <CaretLeft size={20} />
              <div className={classes.datePickerDiv}>
                <MuiPickersUtilsProvider
                  libInstance={moment}
                  utils={MomentUtils}
                >
                  <KeyboardDatePicker
                    disableToolbar
                    invalidDateMessage={"inValid"}
                    variant="inline"
                    format="DD MMM, yyyy"
                    margin="normal"
                    value={selectedStartDate}
                    InputProps={{
                      disableUnderline: true,
                    }}
                    onChange={(date) => {
                      setSelectedStartDate(date);
                    }}
                    id="Startdate-picker"
                    KeyboardButtonProps={{
                      "aria-label": "change date",
                      style: {
                        padding: "0px",
                      },
                    }}
                    className={classes.datePickerComponent}
                    size="small"
                    fullWidth={true}
                  />
                </MuiPickersUtilsProvider>
              </div>
              <span>-</span>
              <div className={classes.datePickerDiv}>
                <MuiPickersUtilsProvider
                  libInstance={moment}
                  utils={MomentUtils}
                >
                  <KeyboardDatePicker
                    disableToolbar
                    invalidDateMessage={"inValid"}
                    variant="inline"
                    format="DD MMM, yyyy"
                    margin="normal"
                    value={selectedEndDate}
                    onChange={(date) => {
                      setSelectedEndDate(date);
                    }}
                    id="Enddate-picker"
                    InputProps={{
                      disableUnderline: true,
                    }}
                    KeyboardButtonProps={{
                      "aria-label": "change date",
                      style: {
                        padding: "0px",
                      },
                    }}
                    className={classes.datePickerComponent}
                    size="small"
                    fullWidth={true}
                  />
                </MuiPickersUtilsProvider>
              </div>
              <CaretRight size={20} />
            </Box>
            {/* <Box className={commonClasses.flexAndGap8Px}>
              <Button color="white" sx={{ minWidth: "0px", padding: "0px" }}>
                <CaretLeft />
              </Button>
              <Typography>2024</Typography>
              <Button color="white" sx={{ minWidth: "0px", padding: "0px" }}>
                <CaretRight />
              </Button>
            </Box> */}
            <Box display="flex" alignItems="center" justifyContent="start">
              <Button
                sx={{
                  color: "#6C65D9",
                  minWidth: 0,
                  padding: 0,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
                onClick={() => setShowChart(!showChart)}
              >
                {showChart ? <EyeSlash size={20} /> : <Eye size={20} />}
                <Typography>
                  {showChart ? "Hide Chart" : "Show Chart"}
                </Typography>
              </Button>
            </Box>
          </Box>

          {/* render tabs based on selected item  */}
          {selectedTab === "score" ? (
            // score logs
            <>
              {showChart ? (
                <Box
                  sx={{
                    height: { lg: "160px", xl: "200px" },
                    borderRadius: "10px",
                  }}
                >
                  <ReactApexChart
                    options={options}
                    series={series}
                    type="bar"
                    height="100%"
                    width="100%"
                  />
                </Box>
              ) : (
                ""
              )}
              <Paper
                sx={{
                  width: "100%",
                  overflow: "hidden",
                  bgcolor: "transparent",
                  color: "white",
                }}
              >
                <TableContainer
                  sx={{
                    height: showChart
                      ? { lg: 330, xl: 420 }
                      : { lg: 490, xl: 620 },
                  }}
                >
                  <Table stickyHeader aria-label="sticky table" sx={{ pr: 2 }}>
                    <TableHead>
                      <TableRow>
                        {columns.map((column) => (
                          <TableCell
                            key={column.id}
                            align={column.align}
                            style={{
                              minWidth: column.minWidth,
                              backgroundColor: "#1B1D28",
                              color: "gray",
                              borderBottom: "1px solid gray",
                              paddingTop: "2px",
                            }}
                          >
                            {column.label}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {userScoreDetails?.records?.length ? (
                        userScoreDetails?.records?.map((row) => {
                          return (
                            <TableRow
                              hover
                              role="checkbox"
                              tabIndex={-1}
                              key={row.code}
                            >
                              <TableCell
                                key={row.created_at}
                                align="left"
                                sx={{ ...commonTableCell }}
                              >
                                {row.added_score}
                              </TableCell>
                              <TableCell
                                key={row.created_at}
                                align="left"
                                sx={{ ...commonTableCell }}
                              >
                                <DateAndTimeViewer
                                  dateAndTime={row.created_at}
                                />
                              </TableCell>
                              <TableCell
                                key={row.created_at}
                                align="center"
                                sx={{ ...commonTableCell }}
                              >
                                {row.task}
                              </TableCell>
                              <TableCell
                                key={row.created_at}
                                align="right"
                                sx={{ ...commonTableCell }}
                              >
                                {row.score_type}
                              </TableCell>
                              {userRoleForProject === "admin" ? (
                                <TableCell
                                  key={row.created_at}
                                  align="right"
                                  sx={{
                                    borderBottom: "1px solid gray",
                                  }}
                                >
                                  <Button
                                    sx={{
                                      color: "red",
                                      minWidth: 0,
                                      padding: 0,
                                    }}
                                    onClick={() =>
                                      setDeleteScoreData({
                                        isModalOpen: true,
                                        selectedScore: row,
                                      })
                                    }
                                  >
                                    <Trash size={22} fill="red" />
                                  </Button>
                                </TableCell>
                              ) : (
                                ""
                              )}
                            </TableRow>
                          );
                        })
                      ) : (
                        <TableRow>
                          <TableCell
                            colSpan={5}
                            align="center"
                            sx={{ borderBottom: "none" }}
                          >
                            <Box
                              className={commonClasses.flexCenter}
                              gap={1}
                              mt={1}
                            >
                              <Info size={30} color="#606479" />
                              <Typography color="#606479" fontSize="20px">
                                No scores were logged during this date.
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </>
          ) : (
            // ranking table
            <UserScoreRankingTable projectId={projectId} />
          )}
        </Box>
      </Grid2>
      {/* right side component start  */}
      <Modal
        className={commonClasses.flexCenter}
        open={deleteScoreData.isModalOpen}
        onClose={() =>
          setDeleteScoreData({
            isModalOpen: false,
            selectedScore: null,
          })
        }
      >
        <DeleteModal
          type="User Score"
          name={deleteScoreData?.selectedScore?.score}
          handleCloseDeleteModal={() =>
            setDeleteScoreData({
              isModalOpen: false,
              selectedScore: null,
            })
          }
          handleDelete={handleDeleteScore}
        />
      </Modal>
    </Grid2>
  );
};

export default UserScoreExpandedView;

const UserSvg = () => {
  return (
    <svg
      width="201"
      height="200"
      viewBox="0 0 201 199"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect x="0.5" width="200" height="185" rx="10" fill="#252A38" />
      <rect
        x="40.5371"
        y="92.6426"
        width="5.18728"
        height="85.0746"
        rx="2.59364"
        fill="#FFBB56"
      />
      <rect
        x="126.559"
        y="74.9375"
        width="5.18729"
        height="100.621"
        rx="2.59364"
        fill="#FFBB56"
      />
      <path
        d="M38.375 103.616C38.375 100.297 40.7058 97.4341 43.956 96.7612L126.353 79.7038C130.697 78.8044 134.772 82.1219 134.772 86.5585V123.13C134.772 126.449 132.441 129.311 129.191 129.984L46.794 147.042C42.4495 147.941 38.375 144.623 38.375 140.187V103.616Z"
        fill="black"
      />
      <rect
        x="165.896"
        y="178.148"
        width="6.47776"
        height="100.72"
        rx="3.23888"
        transform="rotate(90 165.896 178.148)"
        fill="#FFBB56"
      />
      <rect
        x="178"
        y="136.26"
        width="6.47776"
        height="100.72"
        rx="3.23888"
        transform="rotate(90 178 136.26)"
        fill="#FFBB56"
      />
      <path
        d="M27.9064 149.854C32.6214 109.486 76.2827 96.048 96.9255 95.5306C161.574 90.4841 161.416 141.245 161.574 178.15L27.1828 178.15C27.1835 167.803 26.8623 158.793 27.9064 149.854Z"
        fill="#6C65D9"
      />
      <path
        d="M67.3376 110.782C53.677 91.4765 71.8234 81.5778 83.7347 78.6917C84.4552 78.5171 85.1567 79.0066 85.2711 79.7384L86.0732 84.8671C86.1174 85.1498 86.0667 85.4392 85.929 85.69L79.3598 97.6576C79.0383 98.2432 79.221 98.9792 79.7665 99.3653C107.25 118.819 111.54 156.786 111.442 176.671C111.438 177.379 110.86 177.935 110.151 177.935H83.8511C83.1017 177.935 82.5088 177.306 82.5467 176.558C83.3527 160.647 81.1076 130.243 67.3376 110.782Z"
        fill="#A09AFB"
      />
      <path
        d="M129.369 102.361C133.09 90.0066 122.957 84.8709 116.185 83.3594L63.4477 99.1219C58.4333 100.331 57.1797 105.528 57.1797 107.975C62.367 105.902 69.8597 108.839 72.9577 110.566C98.2457 124.666 122.021 126.76 129.369 102.361Z"
        fill="#A09AFB"
      />
      <path
        d="M125.878 137.9C126.553 130.558 118.987 111.508 114.439 101.383C114.174 100.794 114.624 100.137 115.269 100.164L129.34 100.761C129.658 100.775 129.943 100.962 130.082 101.249L148.241 138.831C148.413 139.187 148.323 139.619 148.001 139.85C142.54 143.767 135.519 139.392 126.819 139.025C126.25 139.002 125.826 138.466 125.878 137.9Z"
        fill="#A09AFB"
      />
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M124.137 110.224C124.564 110.438 124.737 110.956 124.524 111.383C122.472 115.483 119.28 118.19 114.843 120.406C114.416 120.62 113.897 120.447 113.683 120.02C113.47 119.593 113.643 119.075 114.07 118.861C118.279 116.759 121.139 114.284 122.977 110.61C123.191 110.184 123.71 110.011 124.137 110.224Z"
        fill="#252A38"
      />
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M87.3799 108.342C87.6544 107.952 88.1937 107.857 88.5843 108.132C96.8036 113.902 101.202 122.966 104.239 130.766C104.412 131.21 104.191 131.711 103.746 131.884C103.301 132.057 102.8 131.836 102.627 131.392C99.6117 123.645 95.365 115.003 87.5902 109.545C87.1995 109.271 87.1054 108.732 87.3799 108.342Z"
        fill="#252A38"
      />
      <path
        d="M74.0648 87.0467C82.3386 82.0873 94.4044 82.2251 99.4031 82.9139C110.435 84.2915 130.223 89.5264 121.122 99.4453C112.021 109.364 94.2321 105.645 86.4754 102.545C78.8912 99.4453 65.7911 92.0061 74.0648 87.0467Z"
        fill="#252A38"
      />
      <path
        d="M83.8902 101.513L83.373 74.1328L112.331 83.4317L113.882 104.096C105.609 108.229 90.0955 104.612 83.8902 101.513Z"
        fill="#E5986D"
      />
      <path
        d="M113.365 95.3135C103.437 96.9666 93.3703 88.7699 89.5781 84.4648L112.848 87.5645L113.365 95.3135Z"
        fill="#252A38"
      />
      <path
        d="M122.326 55.8688C124.93 72.5 122.519 87.2783 110.78 89.1127C90.6128 92.264 82.4183 79.143 79.8144 62.5118C77.2105 45.8807 84.6162 30.9114 96.3556 29.0769C108.095 27.2425 119.722 39.2377 122.326 55.8688Z"
        fill="#E6986D"
      />
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M113.813 47.8958C112.825 48.0423 111.94 48.5572 111.451 48.9756C111.018 49.347 110.365 49.2968 109.993 48.8636C109.621 48.4303 109.672 47.7781 110.105 47.4067C110.823 46.792 112.058 46.067 113.509 45.8518C115.013 45.6289 116.732 45.9623 118.232 47.4606C118.636 47.8641 118.636 48.5183 118.232 48.9218C117.828 49.3252 117.173 49.3252 116.769 48.9218C115.787 47.9404 114.749 47.7571 113.813 47.8958Z"
        fill="#1B1D28"
      />
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M100.692 53.5669C101.262 53.5194 101.771 53.8657 101.828 54.3403L102.346 58.6587C102.403 59.1334 101.987 59.5566 101.417 59.6041C100.847 59.6515 100.339 59.3052 100.282 58.8306L99.763 54.5122C99.706 54.0376 100.122 53.6143 100.692 53.5669Z"
        fill="#1B1D28"
      />
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M115.389 52.27C115.959 52.2225 116.468 52.5688 116.525 53.0434L117.043 57.3618C117.101 57.8365 116.685 58.2597 116.114 58.3072C115.544 58.3546 115.036 58.0084 114.979 57.5337L114.46 53.2153C114.403 52.7407 114.819 52.3174 115.389 52.27Z"
        fill="#1B1D28"
      />
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M102.254 50.2256C99.475 49.5314 97.6743 51.0927 97.1605 51.8627C96.8436 52.3375 96.2015 52.4658 95.7263 52.1493C95.251 51.8328 95.1226 51.1913 95.4394 50.7165C96.3045 49.4201 98.9165 47.2619 102.756 48.2208C103.31 48.3592 103.647 48.9202 103.509 49.4738C103.37 50.0274 102.809 50.364 102.254 50.2256Z"
        fill="#1B1D28"
      />
      <path
        d="M89.9311 59.2443C90.8657 65.2136 90.0004 70.5179 85.7869 71.1763C78.5485 72.3074 75.6073 67.5979 74.6727 61.6286C73.7381 55.6594 76.3962 50.2866 80.6097 49.6282C84.8232 48.9697 88.9965 53.2751 89.9311 59.2443Z"
        fill="#E6986D"
      />
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M78.669 59.8008C79.2402 59.8008 79.7032 60.2634 79.7032 60.834C79.7032 61.5274 79.823 62.5487 80.1463 63.3562C80.4822 64.195 80.8686 64.4502 81.2545 64.4502C81.8257 64.4502 82.2887 64.9128 82.2887 65.4834C82.2887 66.054 81.8257 66.5166 81.2545 66.5166C79.5721 66.5166 78.6656 65.222 78.2258 64.1236C77.7735 62.9938 77.6348 61.6903 77.6348 60.834C77.6348 60.2634 78.0978 59.8008 78.669 59.8008Z"
        fill="#1B1D28"
      />
      <path
        d="M85.9249 53.7773C88.5186 67.1647 91.3715 73.9879 102.784 73.6425C105.852 73.5496 108.501 73.3268 110.781 72.9655L113.374 71.0514L116.832 71.3476C120.017 69.993 121.759 68.1571 122.708 65.5556C122.873 65.1046 123.545 65.1367 123.572 65.6159C123.993 73.0656 121.831 87.8326 111.645 89.8369C101.156 91.9009 88.2732 89.196 83.115 75.8017C78.7924 68.4603 86.5733 69.9717 81.1699 57.8799L85.9249 53.7773Z"
        fill="#C9C9C9"
      />
      <path
        d="M124.031 74.2735C117.77 76.6527 114.495 73.7027 113.388 71.3987C113.224 71.0568 113.262 70.6518 113.487 70.3465C116.118 66.7784 121.185 69.6353 124.36 72.3234C125.039 72.8974 124.862 73.9577 124.031 74.2735Z"
        fill="#ECECEC"
      />
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M110.399 57.0567C110.917 56.9023 111.462 57.1968 111.617 57.7145C112.285 59.9537 113.157 61.6934 113.922 63.2191C114.254 63.8817 114.566 64.5038 114.833 65.1091C115.245 66.0476 115.582 67.0329 115.488 67.9933C115.384 69.0488 114.139 70.3117 113.132 70.9762C112.681 71.2741 112.073 71.1502 111.775 70.6995C111.477 70.2488 111.601 69.642 112.052 69.3442C112.702 68.9155 113.507 68.1272 113.539 67.8018C113.58 67.3813 113.437 66.7988 113.04 65.8961C112.833 65.4251 112.561 64.883 112.256 64.2732C111.47 62.7034 110.46 60.6845 109.74 58.2736C109.586 57.7559 109.881 57.2111 110.399 57.0567Z"
        fill="#1B1D28"
      />
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M108.318 72.3815C108.881 72.227 109.462 72.5609 109.615 73.1273C109.869 74.0626 110.201 75.0011 111.018 75.7377C111.822 76.462 113.255 77.1274 115.992 77.1274C116.576 77.1274 117.049 77.6033 117.049 78.1904C117.049 78.7775 116.576 79.2534 115.992 79.2534C112.919 79.2534 110.918 78.5015 109.608 77.3212C108.312 76.1533 107.851 74.7002 107.577 73.6867C107.423 73.1203 107.755 72.5359 108.318 72.3815Z"
        fill="#1B1D28"
      />
      <path
        d="M98.9267 74.617C107.529 77.1795 112.07 74.1182 113.654 71.6642C113.911 71.2659 113.853 70.7469 113.522 70.4077C109.892 66.6921 102.982 69.6905 98.6189 72.5362C97.7705 73.0895 97.9558 74.3277 98.9267 74.617Z"
        fill="#ECECEC"
      />
      <path
        d="M80.5225 58.0959C71.6735 57.1982 69.6735 47.9145 69.6408 44.8316C61.8521 39.2648 67.859 34.5246 71.836 32.8504C71.836 29.8101 74.2545 24.6276 88.7356 19.0137C106.832 11.9982 120.433 14.9063 126.343 23.7638C130.234 29.5942 129.176 31.5535 127.445 34.0136C117.473 43.7173 103.891 40.5132 98.3468 37.6982C93.2463 45.4109 85.5083 47.2078 82.2768 47.1423C85.4716 50.203 89.3715 58.9936 80.5225 58.0959Z"
        fill="#ECECEC"
      />
      <path
        d="M127.674 150.44L119.454 153.312C118.768 153.552 118.291 154.176 118.241 154.9L117.37 167.45C117.294 168.539 118.191 169.448 119.283 169.388L145.424 167.951C146.308 167.903 147.028 167.226 147.13 166.349L148.799 152.04C148.924 150.961 148.08 150.014 146.993 150.014H137.142C136.138 150.014 135.325 149.202 135.325 148.199V141.362C135.325 140.36 134.511 139.547 133.507 139.547H130.709C129.706 139.547 128.892 140.36 128.892 141.362V148.726C128.892 149.498 128.404 150.185 127.674 150.44Z"
        fill="#E5986D"
      />
      <path
        d="M27.2233 178.37C27.0565 176.528 30.28 162.014 32.0688 161.537L118.859 150.906C121.046 150.323 123.321 151.437 124.049 153.577C125.928 159.098 128.658 169.353 124.943 173.959C119.729 180.425 69.4874 193.486 47.2135 193.486C30.7354 193.486 27.6916 183.544 27.2233 178.37Z"
        fill="#6C64D9"
      />
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M126.066 159.973C126.584 159.914 127.05 160.293 127.108 160.817C127.583 165.158 127.585 165.194 127.585 170.006C127.585 170.534 127.163 170.962 126.642 170.962C126.121 170.962 125.698 170.534 125.698 170.006C125.698 167.512 125.699 166.338 125.631 165.168C125.571 164.109 125.454 163.054 125.232 161.029C125.175 160.504 125.548 160.031 126.066 159.973Z"
        fill="#252A38"
      />
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M59.1596 148.983C59.652 148.902 60.1176 149.231 60.1996 149.718C60.3848 150.817 61.2326 152.767 63.6801 154.165C66.1338 155.566 70.302 156.473 77.2236 155.244L77.244 155.24L114.77 150.32C115.265 150.255 115.719 150.599 115.785 151.089C115.851 151.578 115.503 152.028 115.008 152.093L77.5224 157.008C70.3571 158.277 65.7197 157.395 62.7763 155.713C59.8239 154.027 58.6834 151.595 58.4167 150.012C58.3347 149.525 58.6673 149.064 59.1596 148.983Z"
        fill="#252A38"
      />
    </svg>
  );
};
